import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Search, Users, Trash2, Shield, Menu, MapPin, Lock, Eye } from "lucide-react";

export default function AdminUsers() {
  const { authAxios, user: currentUser, impersonate } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [agentsRegistry, setAgentsRegistry] = useState([]);
  const [editingCodAgente, setEditingCodAgente] = useState(null);
  const [codAgenteValue, setCodAgenteValue] = useState("");
  const [editingCodCli, setEditingCodCli] = useState(null);
  const [codCliValue, setCodCliValue] = useState("");
  const [destDialogUser, setDestDialogUser] = useState(null); // { ...user, destinations: [] }
  const [destDialogLoading, setDestDialogLoading] = useState(false);
  const [destDialogFilter, setDestDialogFilter] = useState("");
  const [editingSeq, setEditingSeq] = useState(null); // userId being edited
  const [seqValue, setSeqValue] = useState("");

  const fetchUsers = useCallback(async () => {
    try {
      const response = await authAxios.get("/users");
      setUsers(response.data);
    } catch {
      toast.error("Errore nel caricamento degli utenti");
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  const fetchAgentsRegistry = useCallback(async () => {
    try {
      const response = await authAxios.get("/agents/registry");
      setAgentsRegistry(response.data);
    } catch (e) { console.error("Failed to fetch agents registry:", e); }
  }, [authAxios]);

  useEffect(() => {
    fetchUsers();
    fetchAgentsRegistry();
  }, [fetchUsers, fetchAgentsRegistry]);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await authAxios.put(`/users/${userId}/role?role=${newRole}`);
      toast.success("Ruolo aggiornato con successo");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante l'aggiornamento");
    }
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    try {
      await authAxios.delete(`/users/${userToDelete.id}`);
      toast.success("Utente eliminato");
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante l'eliminazione");
    }
  };

  const handleSaveCodAgente = async (userId) => {
    try {
      const val = codAgenteValue.trim() === "" ? null : parseInt(codAgenteValue);
      await authAxios.put(`/users/${userId}/cod-agente`, { cod_agente: val });
      toast.success("Codice agente aggiornato");
      setEditingCodAgente(null);
      setCodAgenteValue("");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento");
    }
  };

  const handleSaveCodCli = async (userId) => {
    try {
      const val = codCliValue.trim();
      await authAxios.put(`/users/${userId}/client-link`, { codice_cli: val });
      toast.success(val ? "Cliente associato" : "Associazione rimossa");
      setEditingCodCli(null);
      setCodCliValue("");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento");
    }
  };

  const openDestDialog = async (u) => {
    if (!u.client_id) {
      toast.error("Associare prima un Cod. Cliente");
      return;
    }
    setDestDialogUser({ ...u, destinations: [] });
    setDestDialogFilter("");
    setDestDialogLoading(true);
    try {
      const r = await authAxios.get(`/clients/${u.client_id}/destinations`);
      setDestDialogUser({ ...u, destinations: r.data || [] });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore caricamento destinazioni");
      setDestDialogUser({ ...u, destinations: [] });
    } finally {
      setDestDialogLoading(false);
    }
  };

  const handleAssignDest = async (destId) => {
    if (!destDialogUser) return;
    try {
      await authAxios.put(`/users/${destDialogUser.id}/dest-link`, { dest_id: destId || "" });
      toast.success(destId ? "Destinazione assegnata (utente loccato)" : "Lock destinazione rimosso");
      setDestDialogUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const handleSaveSeq = async (userId) => {
    try {
      const val = seqValue.trim();
      await authAxios.put(`/users/${userId}/numero-seq`, { numero_seq: val });
      toast.success(val ? `Numeratore impostato a ${val}` : "Numeratore rimosso");
      setEditingSeq(null);
      setSeqValue("");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const handleToggleMagazziniere = async (u) => {
    try {
      const enabled = !u.magazziniere_extra;
      await authAxios.put(`/users/${u.id}/magazziniere-extra`, { enabled });
      toast.success(enabled ? `${u.name} ora ha i poteri di magazziniere` : `Poteri magazziniere rimossi da ${u.name}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const handleToggleGeo = async (u) => {
    try {
      const enabled = !u.geo_tracking;
      await authAxios.put(`/users/${u.id}/geo-tracking`, { enabled });
      toast.success(enabled
        ? `${u.name}: posizione ordini attivata (il permesso GPS verrà chiesto una sola volta)`
        : `${u.name}: posizione ordini disattivata`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const handleImpersonate = async (u) => {
    try {
      await impersonate(u.id);
      toast.success(`Modalità demo attiva: stai navigando come ${u.name}`);
      // Redirect alla home del ruolo target così l'admin vede subito l'app
      // come la vedrebbe quell'utente.
      if (u.role === "agent") navigate("/agent/catalog", { replace: true });
      else if (u.role === "client") navigate("/catalog", { replace: true });
      else if (u.role === "magazziniere") navigate("/warehouse", { replace: true });
      else navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossibile avviare la modalità demo");
    }
  };

  const filteredUsers = users.filter(
    (u) =>
      u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const RoleBadge = ({ role }) => {
    const styles = {
      admin: "bg-purple-500/20 text-purple-400",
      agent: "bg-blue-500/20 text-blue-400",
      client: "bg-emerald-500/20 text-emerald-400",
      magazziniere: "bg-orange-500/20 text-orange-400",
    };
    const labels = {
      admin: "Admin",
      agent: "Agente",
      client: "Cliente",
      magazziniere: "Magazziniere",
    };
    return <Badge className={styles[role] || "bg-zinc-500/20 text-zinc-400"}>{labels[role] || role}</Badge>;
  };

  return (
    <div className="min-h-screen bg-[#09090b]">

      <main className="p-4 sm:p-6 lg:p-8">
        <PageHeader title="Utenti" />
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-1">Utenti</h1>
              <p className="text-zinc-400 text-sm sm:text-base">Gestisci gli utenti e i loro ruoli</p>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input
              data-testid="users-search"
              type="text"
              placeholder="Cerca per nome o email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-[#18181b] border-white/10"
            />
          </div>
        </div>

        {/* Role Legend */}
        <div className="mb-6 flex items-center gap-4 flex-wrap">
          <span className="text-sm text-zinc-400">Legenda ruoli:</span>
          <div className="flex items-center gap-2">
            <RoleBadge role="admin" />
            <span className="text-xs text-zinc-500">Vede tutti i prezzi</span>
          </div>
          <div className="flex items-center gap-2">
            <RoleBadge role="agent" />
            <span className="text-xs text-zinc-500">Vede listino + minimo</span>
          </div>
          <div className="flex items-center gap-2">
            <RoleBadge role="client" />
            <span className="text-xs text-zinc-500">Vede solo listino</span>
          </div>
          <div className="flex items-center gap-2">
            <RoleBadge role="magazziniere" />
            <span className="text-xs text-zinc-500">Gestisce ordini</span>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-[#18181b] rounded-xl border border-white/5 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500 mx-auto"></div>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="p-8 text-center">
              <Users className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400">Nessun utente trovato</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-white/5 hover:bg-transparent">
                  <TableHead className="text-zinc-400 uppercase text-xs tracking-wider">Utente</TableHead>
                  <TableHead className="text-zinc-400 uppercase text-xs tracking-wider">Email</TableHead>
                  <TableHead className="text-zinc-400 uppercase text-xs tracking-wider">Ruolo</TableHead>
                  <TableHead className="text-zinc-400 uppercase text-xs tracking-wider">Cod. Agente</TableHead>
                  <TableHead className="text-zinc-400 uppercase text-xs tracking-wider">Numeratore</TableHead>
                  <TableHead className="text-zinc-400 uppercase text-xs tracking-wider">Magazz.</TableHead>
                  <TableHead className="text-zinc-400 uppercase text-xs tracking-wider">Posizione</TableHead>
                  <TableHead className="text-zinc-400 uppercase text-xs tracking-wider">Cod. Cliente</TableHead>
                  <TableHead className="text-zinc-400 uppercase text-xs tracking-wider">Destinatario</TableHead>
                  <TableHead className="text-zinc-400 uppercase text-xs tracking-wider">Data Registrazione</TableHead>
                  <TableHead className="text-zinc-400 uppercase text-xs tracking-wider text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow
                    key={user.id}
                    data-testid={`user-row-${user.id}`}
                    className="border-white/5"
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500/20 to-amber-600/20 flex items-center justify-center">
                          <span className="text-amber-400 font-medium">
                            {user.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-white">{user.name}</span>
                          {user.id === currentUser.id && (
                            <Badge variant="outline" className="ml-2 text-xs border-amber-500/50 text-amber-400">
                              Tu
                            </Badge>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-zinc-400">{user.email}</TableCell>
                    <TableCell>
                      {user.id === currentUser.id ? (
                        <RoleBadge role={user.role} />
                      ) : (
                        <Select
                          value={user.role}
                          onValueChange={(val) => handleRoleChange(user.id, val)}
                        >
                          <SelectTrigger
                            data-testid={`role-select-${user.id}`}
                            className="w-32 bg-transparent border-white/10"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#27272a] border-white/10">
                            <SelectItem value="admin">
                              <div className="flex items-center gap-2">
                                <Shield className="w-3 h-3 text-purple-400" />
                                Admin
                              </div>
                            </SelectItem>
                            <SelectItem value="agent">
                              <div className="flex items-center gap-2">
                                <Shield className="w-3 h-3 text-blue-400" />
                                Agente
                              </div>
                            </SelectItem>
                            <SelectItem value="client">
                              <div className="flex items-center gap-2">
                                <Shield className="w-3 h-3 text-emerald-400" />
                                Cliente
                              </div>
                            </SelectItem>
                            <SelectItem value="magazziniere">
                              <div className="flex items-center gap-2">
                                <Shield className="w-3 h-3 text-orange-400" />
                                Magazziniere
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </TableCell>
                    <TableCell>
                      {user.role === "agent" ? (
                        editingCodAgente === user.id ? (
                          <div className="flex items-center gap-1">
                            <Input
                              type="number"
                              value={codAgenteValue}
                              onChange={(e) => setCodAgenteValue(e.target.value)}
                              className="w-20 h-7 bg-[#09090b] border-white/10 text-white text-sm"
                              data-testid={`cod-agente-input-${user.id}`}
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === "Enter") handleSaveCodAgente(user.id);
                                if (e.key === "Escape") setEditingCodAgente(null);
                              }}
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-emerald-400 hover:text-emerald-300"
                              onClick={() => handleSaveCodAgente(user.id)}
                              data-testid={`save-cod-agente-${user.id}`}
                            >
                              OK
                            </Button>
                          </div>
                        ) : (
                          <button
                            className="text-sm font-mono cursor-pointer hover:text-amber-400 transition-colors"
                            data-testid={`edit-cod-agente-${user.id}`}
                            onClick={() => {
                              setEditingCodAgente(user.id);
                              setCodAgenteValue(user.cod_agente != null ? String(user.cod_agente) : "");
                            }}
                          >
                            {user.cod_agente != null ? (
                              <span className="text-blue-400">{user.cod_agente}</span>
                            ) : (
                              <span className="text-zinc-600 italic">Assegna</span>
                            )}
                          </button>
                        )
                      ) : (
                        <span className="text-zinc-600">-</span>
                      )}
                    </TableCell>
                    {/* Numeratore (solo agenti) */}
                    <TableCell>
                      {user.role === "agent" ? (
                        editingSeq === user.id ? (
                          <div className="flex items-center gap-1">
                            <Input
                              type="number"
                              min="1"
                              max="9"
                              value={seqValue}
                              onChange={(e) => setSeqValue(e.target.value)}
                              placeholder="1-9"
                              className="w-16 h-7 bg-[#09090b] border-white/10 text-white text-sm font-mono no-spin"
                              data-testid={`seq-input-${user.id}`}
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === "Enter") handleSaveSeq(user.id);
                                if (e.key === "Escape") setEditingSeq(null);
                              }}
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-emerald-400 hover:text-emerald-300"
                              onClick={() => handleSaveSeq(user.id)}
                              data-testid={`save-seq-${user.id}`}
                            >
                              OK
                            </Button>
                          </div>
                        ) : (
                          <button
                            className="text-sm font-mono cursor-pointer hover:text-amber-400 transition-colors"
                            data-testid={`edit-seq-${user.id}`}
                            onClick={() => {
                              setEditingSeq(user.id);
                              setSeqValue(user.numero_seq != null ? String(user.numero_seq) : "");
                            }}
                          >
                            {user.numero_seq != null ? (
                              <span className="text-amber-300 bg-amber-500/10 px-1.5 py-0.5 rounded">
                                {user.numero_seq}xxxxx
                              </span>
                            ) : (
                              <span className="text-zinc-600 italic">-</span>
                            )}
                          </button>
                        )
                      ) : (
                        <span className="text-zinc-600">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {user.role === "agent" ? (
                        <button
                          onClick={() => handleToggleMagazziniere(user)}
                          data-testid={`toggle-magazz-${user.id}`}
                          title={user.magazziniere_extra ? "Disattiva poteri magazziniere" : "Attiva poteri magazziniere"}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                            user.magazziniere_extra
                              ? "bg-violet-500/20 text-violet-300 hover:bg-violet-500/30 border border-violet-500/40"
                              : "bg-zinc-800 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-700 border border-white/10"
                          }`}
                        >
                          {user.magazziniere_extra ? "ON" : "OFF"}
                        </button>
                      ) : (
                        <span className="text-zinc-600">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <button
                        onClick={() => handleToggleGeo(user)}
                        data-testid={`toggle-geo-${user.id}`}
                        title={user.geo_tracking
                          ? "Disattiva la registrazione della posizione degli ordini"
                          : "Attiva la registrazione della posizione: all'utente verrà chiesto il permesso GPS una sola volta"}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                          user.geo_tracking
                            ? "bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 border border-emerald-500/40"
                            : "bg-zinc-800 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-700 border border-white/10"
                        }`}
                      >
                        {user.geo_tracking ? "ON" : "OFF"}
                      </button>
                    </TableCell>
                    <TableCell>
                      {user.role === "client" ? (
                        editingCodCli === user.id ? (
                          <div className="flex items-center gap-1">
                            <Input
                              type="text"
                              value={codCliValue}
                              onChange={(e) => setCodCliValue(e.target.value)}
                              placeholder="es. AA301982"
                              className="w-28 h-7 bg-[#09090b] border-white/10 text-white text-sm font-mono"
                              data-testid={`cod-cli-input-${user.id}`}
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === "Enter") handleSaveCodCli(user.id);
                                if (e.key === "Escape") setEditingCodCli(null);
                              }}
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-emerald-400 hover:text-emerald-300"
                              onClick={() => handleSaveCodCli(user.id)}
                              data-testid={`save-cod-cli-${user.id}`}
                            >
                              OK
                            </Button>
                          </div>
                        ) : (
                          <button
                            className="text-sm font-mono cursor-pointer hover:text-emerald-400 transition-colors"
                            data-testid={`edit-cod-cli-${user.id}`}
                            onClick={() => {
                              setEditingCodCli(user.id);
                              setCodCliValue(user.codice_cli || "");
                            }}
                          >
                            {user.codice_cli ? (
                              <span className="text-emerald-400">{user.codice_cli}</span>
                            ) : (
                              <span className="text-zinc-600 italic">Assegna</span>
                            )}
                          </button>
                        )
                      ) : (
                        <span className="text-zinc-600">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {user.role === "client" ? (
                        user.client_id ? (
                          <button
                            type="button"
                            data-testid={`edit-dest-${user.id}`}
                            onClick={() => openDestDialog(user)}
                            className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs transition ${
                              user.dest_id
                                ? "bg-amber-500/15 text-amber-300 hover:bg-amber-500/25"
                                : "bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white"
                            }`}
                          >
                            {user.dest_id ? <Lock className="w-3 h-3" /> : <MapPin className="w-3 h-3" />}
                            {user.dest_id ? "Loccato" : "Libero"}
                          </button>
                        ) : (
                          <span className="text-zinc-700 text-xs italic">- (assegna cliente)</span>
                        )
                      ) : (
                        <span className="text-zinc-600">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-zinc-400 text-sm">
                      {new Date(user.created_at).toLocaleDateString("it-IT", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1">
                        {user.id !== currentUser.id && user.role !== "admin" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`impersonate-user-${user.id}`}
                            onClick={() => handleImpersonate(user)}
                            className="text-zinc-400 hover:text-fuchsia-400 hover:bg-fuchsia-500/10"
                            title={`Vedi l'app come ${user.name}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        )}
                        {user.id !== currentUser.id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`delete-user-${user.id}`}
                            onClick={() => {
                              setUserToDelete(user);
                              setDeleteDialogOpen(true);
                            }}
                            className="text-zinc-400 hover:text-red-400"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Destination assignment dialog (admin → user lock) */}
        <Dialog open={!!destDialogUser} onOpenChange={(o) => !o && setDestDialogUser(null)}>
          <DialogContent className="bg-[#18181b] border-white/10 max-w-lg max-h-[80vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="text-white">Assegna destinazione</DialogTitle>
              <DialogDescription className="text-zinc-400">
                Lock l'utente <span className="text-amber-400 font-semibold">{destDialogUser?.name}</span>{" "}
                a una destinazione specifica del cliente. Se non assegni nulla, l'utente sceglie a runtime.
              </DialogDescription>
            </DialogHeader>
            <div className="my-2">
              <Input
                value={destDialogFilter}
                onChange={(e) => setDestDialogFilter(e.target.value)}
                placeholder="Cerca per nome, indirizzo, città..."
                className="bg-[#0f0f11] border-white/10"
                data-testid="dest-dialog-search"
              />
            </div>
            <div className="flex-1 overflow-y-auto divide-y divide-white/5 -mx-4 px-4">
              {destDialogLoading ? (
                <p className="text-center text-sm text-zinc-500 py-6">Caricamento...</p>
              ) : (destDialogUser?.destinations || []).filter((d) => {
                const q = destDialogFilter.trim().toLowerCase();
                if (!q) return true;
                return [d.codice_dest, d.denominazione, d.indirizzo, d.citta]
                  .filter(Boolean).join(" ").toLowerCase().includes(q);
              }).map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => handleAssignDest(d.id)}
                  data-testid={`dest-dialog-option-${d.id}`}
                  className={`w-full text-left py-2.5 hover:bg-white/5 transition flex items-start gap-2 ${
                    destDialogUser?.dest_id === d.id ? "bg-amber-500/10" : ""
                  }`}
                >
                  <MapPin className="w-4 h-4 text-cyan-400 mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-white truncate">
                      {d.denominazione || d.indirizzo || `Dest. ${d.codice_dest}`}
                    </p>
                    <p className="text-[11px] text-zinc-400 truncate">
                      {[d.indirizzo, d.cap, d.citta, d.provincia ? `(${d.provincia})` : null]
                        .filter(Boolean).join(" · ")}
                    </p>
                    <p className="text-[10px] text-zinc-600 font-mono">Cod. {d.codice_dest}</p>
                  </div>
                  {destDialogUser?.dest_id === d.id && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 font-bold uppercase">
                      Attiva
                    </span>
                  )}
                </button>
              ))}
            </div>
            <DialogFooter className="border-t border-white/5 pt-3 flex-row !justify-between">
              <Button
                variant="outline"
                onClick={() => handleAssignDest(null)}
                disabled={!destDialogUser?.dest_id}
                className="border-white/10 text-zinc-300 hover:text-white"
                data-testid="dest-dialog-unlock"
              >
                Rimuovi lock
              </Button>
              <Button
                variant="outline"
                onClick={() => setDestDialogUser(null)}
                className="border-white/10"
              >
                Chiudi
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="bg-[#18181b] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">Conferma Eliminazione</DialogTitle>
              <DialogDescription className="text-zinc-400">
                Sei sicuro di voler eliminare l'utente "{userToDelete?.name}" ({userToDelete?.email})? Questa azione non puo essere annullata.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDeleteDialogOpen(false)}
                className="border-white/10"
              >
                Annulla
              </Button>
              <Button
                data-testid="confirm-delete-user-btn"
                onClick={handleDelete}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                Elimina
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
